import React from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
} from '@tanstack/react-table';

// ✅ Types
export interface ResidentPaymentHistory {
  id: string;
  date: string;
  description: string;
  amount: string;
  paymentStatus: 'PAID' | 'PENDING' | 'OVERDUE';
}

interface ResidentPaymentHistoryTableProps {
  data: ResidentPaymentHistory[];
  columns?: ColumnDef<ResidentPaymentHistory, any>[];
  isLoading?: boolean;
  className?: string;
}

export const ResidentPaymentHistoryTable: React.FC<
  ResidentPaymentHistoryTableProps
> = ({
  data,
  columns = residentPaymentHistoryColumns,
  isLoading,
  className = '',
}) => {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  });

  // Ensure consistent height to prevent pagination shift
  const visibleRows = table.getRowModel().rows;
  // const paddedRows = [
  //   ...visibleRows,
  //   ...Array(Math.max(0, 5 - visibleRows.length)).fill(null),
  // ];

  if (isLoading)
    return (
      <div className="p-4 space-y-2">
        {[...Array(10)].map((_, i) => (
          <div key={i} className="h-10 bg-gray-300 animate-pulse rounded" />
        ))}
      </div>
    );

  return (
    <div
      className={`min-w-3xl ${className}`}
      style={{ scrollbarWidth: 'none' }}
    >
      <table className="w-full">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id} className="p-2 md:p-4 text-sm md:text-base text-center border">
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {visibleRows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className="p-2 md:p-4 text-sm md:text-base text-start border max-w-60">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
          {!visibleRows.length && (
            <tr>
              <td
                colSpan={columns.length}
                className="py-6 text-center w-full text-gray-400"
              >
                <div className="text-center">
                  No payments found.
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

// ✅ Columns for Managers
const residentPaymentHistoryColumns: ColumnDef<ResidentPaymentHistory>[] = [
  {
    accessorKey: 'date',
    header: () => <div className=" text-center font-bold">Date</div>,
    cell: (info) => <div className="">{String(info.getValue() ?? '-')}</div>,
  },
  {
    accessorKey: 'description',
    header: () => <div className="text-center font-bold">Description</div>,
    cell: (info) => <div className="">{String(info.getValue() ?? '-')}</div>,
  },
  {
    accessorKey: 'amount',
    header: () => <div className="text-center font-bold">Amount</div>,
    cell: (info) => (
      <div className="text-right">{String(info.getValue() ?? '-')}</div>
    ),
  },
  {
    accessorKey: 'paymentStatus',
    header: () => <div className="text-center font-bold">Status</div>,
    cell: (info) => (
      <div
        className={`
        text-center
          ${
            info.getValue() === 'PAID'
              ? 'text-[#177D3D]'
              : info.getValue() === 'OVERDUE'
              ? 'text-[#891518]'
              : 'text-[#987D06]'
          }
          `}
      >
        {info.getValue() === 'PAID'
          ? 'Paid'
          : info.getValue() === 'OVERDUE'
          ? 'Overdue'
          : 'Pending'}
      </div>
    ),
  },
];
